import { css } from 'styled-components';

const DESIGN_WIDTH = 414;
const ADMIN_DESIGN_WIDTH = 1440;

// PX represents 1px in the admin design width, must match --apx in styiles/variables.css
export const APX = Math.max(1, window.innerWidth / ADMIN_DESIGN_WIDTH);

// PX represents 1px in the design width, must match --px in styiles/variables.css 454 635
export const PX = window.innerWidth < 1280 ? Math.min(1.3, window.innerWidth / DESIGN_WIDTH) : APX;

export const getAPX = (windowWidth: number) => Math.max(1, windowWidth / ADMIN_DESIGN_WIDTH);

export const WrapperSize = DESIGN_WIDTH * PX;

export const px = (value: number) => `calc(var(--px) * ${value})`;
export const apx = (value: number) => `calc(var(--apx) * ${value})`;

type TFontFamily = 'Black' | 'Heavy' | 'Bold' | 'Medium' | 'Regular' | 'Light';

export const fontFamilySize = (family: TFontFamily = 'Medium', size: number = 14) => {
  return `
  font-family: 'DINNextLTPro-${family}';
  font-size: ${px(size)};
  `;
};

export const colors = {
  black: 'var(--black)',
  cbg: 'var(--c-bg)',
  dark: 'var(--dark)',
  green: 'var(--green)',
  grey3a: 'var(--grey3a)',
  grey4a: 'var(--grey4a)',
  grey59: 'var(--grey59)',
  grey87: 'var(--grey87)',
  grey8e: 'var(--grey8e)',
  grey97: 'var(--grey97)',
  grey9b: 'var(--grey9b)',
  greyd8: 'var(--greyd8)',
  greydd: 'var(--greydd)',
  greyee: 'var(--greyee)',
  greyf9: 'var(--greyf9)',
  lightBlue: 'var(--lightBlue)',
  mainColor: 'var(--mainColor)',
  mainHoverColor: 'var(--mainHoverColor)',
  nextEventColor: 'var(--nextEventColor)',
  opacityHoverColor: 'var(--opacityHoverColor)',
  orange: 'var(--orange)',
  orangeError: 'var(--orangeError)',
  red: 'var(--red)',
  red3b: 'var(--red3b)',
  red45: 'var(--red45)',
  secondaryColor: 'var(--secondaryColor)',
  teal: 'var(--teal)',
  teal2: 'var(--teal2)',
  transparent: 'var(--transparent)'
};

// Mixings

export const centeredTextMixin = css`
  position: relative;
  top: 0.08em;
`;

/* Desktop Mixins */
export const h1DesktopMixin = css`
  ${fontFamilySize('Black', 36)};
  color: ${colors.dark};
`;

export const h2DesktopMixin = css`
  ${fontFamilySize('Heavy', 30)};
  color: ${colors.dark};
`;

export const h3DesktopMixin = css`
  ${fontFamilySize('Heavy', 22)};
  color: ${colors.dark};
`;

export const h4DesktopMixin = css`
  ${fontFamilySize('Medium', 18)};
  color: ${colors.dark};
`;

export const h5DesktopMixin = css`
  ${fontFamilySize('Heavy', 16)};
  color: ${colors.dark};
`;

export const h6DesktopMixin = css`
  ${fontFamilySize('Bold', 16)};
  color: ${colors.dark};
`;

export const h7DesktopMixin = css`
  ${fontFamilySize('Medium', 16)};
  color: ${colors.dark};
`;

export const h7DesktopItalicMixin = css`
  ${fontFamilySize('Regular', 16)};
  font-style: italic;
  color: ${colors.grey4a};
`;

export const h8DesktopMediumMixin = css`
  ${fontFamilySize('Medium', 14)};
  color: ${colors.dark};
`;

export const h8DesktopBoldMixin = css`
  ${fontFamilySize('Bold', 14)};
  color: ${colors.dark};
`;

export const errorTextMixin = css`
  ${fontFamilySize('Regular', 14)};
  font-style: italic;
  color: ${colors.orangeError};
`;

export const h9InputInlineMixin = css`
  ${fontFamilySize('Medium', 14)};
  color: ${colors.grey9b};
  text-transform: uppercase;
`;

export const cta1DesktopMixin = css`
  ${fontFamilySize('Heavy', 36)};
  color: ${colors.mainColor};
`;

export const cta2DesktopMixin = css`
  ${fontFamilySize('Heavy', 16)};
  color: ${colors.mainColor};
`;

export const cta3DesktopMixin = css`
  ${fontFamilySize('Heavy', 14)};
  color: ${colors.mainColor};
`;

/* Mobile Mixins */
export const h1MobileMixin = css`
  ${fontFamilySize('Heavy', 30)};
  color: ${colors.dark};
`;

export const h2MobileMixin = css`
  ${fontFamilySize('Heavy', 20)};
  color: ${colors.dark};
`;

export const h3MobileMixin = css`
  ${fontFamilySize('Heavy', 18)};
  color: ${colors.dark};
`;

export const h4MobileMixin = css`
  ${fontFamilySize('Medium', 18)};
  color: ${colors.dark};
`;

export const h5MobileMixin = css`
  ${fontFamilySize('Heavy', 16)};
  color: ${colors.dark};
`;

export const h6MobileMixin = css`
  ${fontFamilySize('Bold', 16)};
  color: ${colors.dark};
`;

export const h7MobileMixin = css`
  ${fontFamilySize('Medium', 16)};
  line-height: ${px(20)};
  color: ${colors.dark};
`;

export const h7MobileItalicMixin = css`
  font-family: 'DINNextLTPro-Regular';
  font-style: italic;
  font-size: ${px(16)};
  line-height: ${px(20)};
  color: ${colors.grey4a};
`;

export const h8MobileMediumMixin = css`
  ${fontFamilySize('Medium', 14)};
  color: ${colors.dark};
`;

export const h8MobileBoldMixin = css`
  ${fontFamilySize('Bold', 14)};
  color: ${colors.dark};
`;

export const h9MobileMixin = css`
  ${fontFamilySize('Medium', 14)};
  color: ${colors.grey9b};
  text-transform: uppercase;
`;

export const cta1MobileMixin = css`
  ${fontFamilySize('Heavy', 18)};
  color: ${colors.mainColor};
`;

export const cta2MobileMixin = css`
  ${fontFamilySize('Heavy', 16)};
  color: ${colors.mainColor};
`;

export const cta3MobileMixin = css`
  ${fontFamilySize('Heavy', 14)};
  color: ${colors.mainColor};
`;

export const h8BodyMobileMixin = css`
  ${fontFamilySize('Medium', 14)};
  line-height: ${px(20)};
  color: ${colors.dark};
`;
